import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Loader2,
  Search,
  RefreshCw,
  ExternalLink,
  Download,
  SkipForward,
  BookmarkPlus,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiError } from '@/services/api';
import { useApplicants } from '@/contexts/ApplicantContext';
import { StatusBadge } from '@/components/StatusBadge';
import { relativeTime } from '@/lib/format';
import { useLocalStorage } from '@/hooks/useLocalStorage';
import type { Job, JobStatus } from '@/services/types';

const STATUSES: JobStatus[] = ['new', 'applying', 'applied', 'failed', 'skipped', 'interview', 'offer', 'rejected'];
const SOURCES = ['greenhouse', 'lever', 'ashby', 'workday_api', 'manual'];

interface SavedView {
  name: string;
  filters: {
    status: JobStatus | '';
    source: string;
    q: string;
    from: string;
    to: string;
    tag: string;
  };
}

export default function ApplicationsPage() {
  const { current } = useApplicants();
  const [status, setStatus] = useState<JobStatus | ''>('');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [tag, setTag] = useState('');
  const [offset, setOffset] = useState(0);
  const limit = 50;
  const [rows, setRows] = useState<Job[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [savedViews, setSavedViews] = useLocalStorage<SavedView[]>(
    'jobapplier_admin_saved_views',
    [],
  );
  const [availableTags, setAvailableTags] = useState<string[]>([]);

  const filtersDirty = useMemo(
    () => Boolean(status || source || q || from || to || tag),
    [status, source, q, from, to, tag],
  );

  function buildParams(includeOffset = true) {
    const params = new URLSearchParams({ applicant_id: current });
    if (includeOffset) {
      params.set('limit', String(limit));
      params.set('offset', String(offset));
    }
    if (status) params.set('status', status);
    if (source) params.set('source', source);
    if (q.trim()) params.set('q', q.trim());
    if (from) params.set('from', `${from}T00:00:00.000Z`);
    if (to) params.set('to', `${to}T23:59:59.999Z`);
    if (tag) params.set('tag', tag);
    return params;
  }

  function applyView(v: SavedView) {
    setStatus(v.filters.status);
    setSource(v.filters.source);
    setQ(v.filters.q);
    setFrom(v.filters.from);
    setTo(v.filters.to);
    setTag(v.filters.tag);
    setOffset(0);
  }

  function saveCurrentView() {
    const name = prompt('Name this view (e.g. "Today\'s failures"):');
    if (!name) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const next: SavedView = {
      name: trimmed,
      filters: { status, source, q, from, to, tag },
    };
    const without = savedViews.filter((v) => v.name !== trimmed);
    setSavedViews([...without, next]);
    toast.success(`Saved view "${trimmed}"`);
  }

  function deleteView(name: string) {
    setSavedViews(savedViews.filter((v) => v.name !== name));
    toast.success('View removed');
  }

  function clearFilters() {
    setStatus('');
    setSource('');
    setQ('');
    setFrom('');
    setTo('');
    setTag('');
    setOffset(0);
  }

  async function load() {
    setLoading(true);
    try {
      const params = buildParams();
      const r = await api.get<{ items: Job[]; pagination: { total: number } }>(`/jobs?${params}`);
      setRows(r.items);
      setTotal(r.pagination.total);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'load_failed');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    setOffset(0);
  }, [current, status, source, from, to, tag]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, status, source, from, to, tag, offset]);

  useEffect(() => {
    api
      .get<{ tags: string[] }>(`/jobs/-/tags?applicant_id=${current}`)
      .then((r) => setAvailableTags(r.tags))
      .catch(() => setAvailableTags([]));
  }, [current]);

  async function retry(jobId: number) {
    // Optimistic: flip row to 'new' immediately; revert if the API rejects.
    const prevRows = rows;
    setRows((rs) => rs.map((r) => (r.id === jobId ? { ...r, status: 'new' as const } : r)));
    try {
      await api.post(`/jobs/${jobId}/retry`);
      toast.success(`Retrying #${jobId}`);
    } catch (e) {
      setRows(prevRows);
      toast.error(e instanceof ApiError ? e.message : 'retry_failed');
    }
  }

  async function bulkAction(action: 'retry' | 'skip') {
    if (!selected.size) return;
    const target: Job['status'] = action === 'retry' ? 'new' : 'skipped';
    const prevRows = rows;
    setRows((rs) => rs.map((r) => (selected.has(r.id) ? { ...r, status: target } : r)));
    const ids = Array.from(selected);
    let ok = 0;
    let failed = 0;
    await Promise.all(
      ids.map((id) =>
        (action === 'retry'
          ? api.post(`/jobs/${id}/retry`)
          : api.post(`/jobs/${id}/skip`, { reason: 'bulk skip from admin' })
        )
          .then(() => {
            ok += 1;
          })
          .catch(() => {
            failed += 1;
          }),
      ),
    );
    if (failed > 0) {
      // At least one failed: re-sync from server to get truthful state.
      setRows(prevRows);
      toast.error(`${ok}/${ids.length} succeeded — re-fetching`);
      load();
    } else {
      toast.success(`${action === 'retry' ? 'Retried' : 'Skipped'} ${ok}`);
    }
    setSelected(new Set());
  }

  function exportCsv() {
    const params = buildParams(false);
    const url = `/api/jobs/export.csv?${params}`;
    // Use anchor download to keep session cookies attached
    const a = document.createElement('a');
    a.href = url;
    a.download = '';
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('CSV download started');
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold tracking-tight">Applications</h2>
        <button onClick={load} className="btn-secondary">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && load()}
            placeholder="Search company, title, location…"
            className="input w-72 pl-7"
          />
        </div>
        <select value={status} onChange={(e) => setStatus(e.target.value as JobStatus)} className="input w-40">
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} className="input w-36">
          <option value="">All sources</option>
          {SOURCES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="input w-36"
          title="From date"
        />
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="input w-36"
          title="To date"
        />
        {availableTags.length > 0 && (
          <select
            value={tag}
            onChange={(e) => setTag(e.target.value)}
            className="input w-36"
            title="Filter by tag"
          >
            <option value="">All tags</option>
            {availableTags.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        )}
        <button onClick={exportCsv} className="btn-secondary">
          <Download className="h-4 w-4" />
          CSV
        </button>
        <button
          onClick={saveCurrentView}
          disabled={!filtersDirty}
          className="btn-secondary"
          title="Save current filter combination as a named view"
        >
          <BookmarkPlus className="h-4 w-4" />
          Save view
        </button>
        {filtersDirty && (
          <button onClick={clearFilters} className="btn-secondary text-xs" title="Clear all filters">
            <X className="h-3 w-3" />
            Clear
          </button>
        )}
        {selected.size > 0 && (
          <div className="ml-auto flex gap-2">
            <button onClick={() => bulkAction('retry')} className="btn-primary">
              <RefreshCw className="h-4 w-4" />
              Retry {selected.size}
            </button>
            <button onClick={() => bulkAction('skip')} className="btn-secondary">
              <SkipForward className="h-4 w-4" />
              Skip {selected.size}
            </button>
          </div>
        )}
      </div>

      {savedViews.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="text-gray-500">Saved views:</span>
          {savedViews.map((v) => (
            <span key={v.name} className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 dark:bg-primary-500/10">
              <button
                onClick={() => applyView(v)}
                className="text-primary-700 dark:text-primary-300"
                title={JSON.stringify(v.filters)}
              >
                {v.name}
              </button>
              <button
                onClick={() => deleteView(v.name)}
                className="text-primary-700/60 hover:text-primary-700 dark:text-primary-300/60"
                title="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="card overflow-hidden md:hidden">
        {rows.length === 0 && !loading && (
          <p className="p-4 text-center text-sm text-gray-500">No applications match.</p>
        )}
        <ul className="divide-y divide-gray-100 dark:divide-gray-800">
          {rows.map((j) => (
            <li key={j.id} className="p-3">
              <Link to={`/applications/${j.id}`} className="flex flex-col gap-1">
                <div className="flex items-start justify-between gap-2">
                  <span className="font-medium text-gray-800 dark:text-gray-100">{j.company ?? '—'}</span>
                  <StatusBadge status={j.status} />
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-300">{j.title}</span>
                <span className="text-xs text-gray-500">
                  {j.source} · {relativeTime(j.applied_at ?? j.discovered_at)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      </div>

      <div className="card hidden overflow-hidden md:block">
        <table className="w-full text-sm">
          <thead className="border-b border-gray-100 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
            <tr>
              <th className="w-8 px-3 py-2">
                <input
                  type="checkbox"
                  checked={rows.length > 0 && rows.every((r) => selected.has(r.id))}
                  onChange={(e) =>
                    setSelected(e.target.checked ? new Set(rows.map((r) => r.id)) : new Set())
                  }
                />
              </th>
              <th className="px-3 py-2">Company</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Source</th>
              <th className="px-3 py-2">Applied</th>
              <th className="w-8 px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr>
                <td colSpan={7} className="p-6 text-center text-sm text-gray-500">
                  No applications match.
                </td>
              </tr>
            )}
            {rows.map((j) => (
              <tr key={j.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(j.id)}
                    onChange={(e) => {
                      const next = new Set(selected);
                      if (e.target.checked) next.add(j.id);
                      else next.delete(j.id);
                      setSelected(next);
                    }}
                  />
                </td>
                <td className="px-3 py-2 font-medium text-gray-800">
                  <Link to={`/applications/${j.id}`} className="hover:text-primary-700">
                    {j.company ?? '—'}
                  </Link>
                </td>
                <td className="px-3 py-2 text-gray-700">
                  <Link to={`/applications/${j.id}`} className="hover:underline">
                    {j.title ?? '—'}
                  </Link>
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={j.status} />
                </td>
                <td className="px-3 py-2 text-xs text-gray-500">{j.source}</td>
                <td className="px-3 py-2 text-xs text-gray-500">
                  {relativeTime(j.applied_at ?? j.discovered_at)}
                </td>
                <td className="px-3 py-2 text-right">
                  {(j.status === 'failed' || j.status === 'skipped') && (
                    <button
                      title="Retry"
                      onClick={() => retry(j.id)}
                      className="rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-primary-700"
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {j.url && (
                    <a
                      href={j.url}
                      target="_blank"
                      rel="noreferrer"
                      title="Open job posting"
                      className="ml-1 inline-block rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-primary-700"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {loading && (
          <div className="grid place-items-center border-t border-gray-100 p-4 text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
      </div>

      <Pagination total={total} offset={offset} limit={limit} onOffset={setOffset} />
    </div>
  );
}

function Pagination({
  total,
  offset,
  limit,
  onOffset,
}: {
  total: number;
  offset: number;
  limit: number;
  onOffset: (n: number) => void;
}) {
  const start = offset + 1;
  const end = Math.min(offset + limit, total);
  return (
    <div className="flex items-center justify-between text-sm text-gray-600">
      <span>
        {total === 0 ? '0' : `${start}–${end}`} of {total.toLocaleString()}
      </span>
      <div className="flex gap-2">
        <button
          disabled={offset === 0}
          onClick={() => onOffset(Math.max(0, offset - limit))}
          className="btn-secondary"
        >
          Prev
        </button>
        <button
          disabled={end >= total}
          onClick={() => onOffset(offset + limit)}
          className="btn-secondary"
        >
          Next
        </button>
      </div>
    </div>
  );
}
